import { inkfree, mobile, signer } from "theme/colors";

// Shared styling constants for the split-screen auth screens (Login, Register).
//
// Brand colors (ink, orange, purple, coral, amber) come from theme/colors. The
// values below are the ones in the Auth Screens reference that have no exact
// match there — kept verbatim and scoped to these screens for now rather than
// being promoted into the global palette.
export const authRef = {
  subText: signer.text.meta, // secondary body copy
  metaText: signer.text.faint, // muted meta copy, divider label, footer
  inputBg: "#F4F6FD", // field fill
  inputBorder: "#D8DEF0", // field + secondary-button border
  inputBorderHover: "#B9C2E0", // field + secondary-button border, hovered
  dividerLine: "#E8E5F0", // "or continue with" / "or with email" rule
  // Centered-card screens (reset password, verify email, set password, ...)
  pageBg: "#FAFAFC", // canvas behind the card
  cardBorder: "#ECE9F2", // card hairline
  cardShadow: "0 8px 32px rgba(33,21,71,0.07)",
  footerText: signer.text.faint, // legal line below the card (WCAG 1.4.3, was #B4B0C2 at 2.11:1)
  // "Signed in with Google" chip on the OAuth profile screen (reference 3c)
  chipBorder: "#E3E8F6",
  successBadgeBg: "#E7F6EC",
  successBadgeIcon: "#1F8A4C",
  // Shield badge on the two-factor screen (reference 3g)
  badgeBgPurple: "#F4EDFB",
  // 2FA setup (reference 3f) — admin-policy banner and numbered steps.
  // The QR frame and secret-key box reuse `chipBorder` (same hairline).
  noticeTextPurple: "#584680",
  stepText: "#4A4463",
  // Password expiry (reference 3h) — amber icon circle behind the clock.
  warnTint: "#FDF3D7",
};

// Amber "check your spam" callout on the verify-email screen (reference 3a).
export const authNotice = {
  border: "#F0C9A8",
  bg: "#FEF9F3",
  icon: "#D98324",
  text: "#8A6B3F",
  strong: "#6E5227",
};

// Type scale lifted from the reference (px → rem at a 16px root).
export const refFont = {
  heading: "1.875rem", // 30px  Log in
  headingCompact: "1.75rem", // 28px  Create your account
  body: "0.84375rem", // 13.5px  intro copy, field text
  label: "0.78125rem", // 12.5px  field labels
  link: "0.75rem", // 12px  forgot password
  button: "0.9375rem", // 15px  primary button
  socialButton: "0.8125rem", // 13px  google / passkey
  divider: "0.6875rem", // 11px  OR CONTINUE WITH / OR WITH EMAIL
  legal: "0.6875rem", // 11px  terms + privacy consent line
  footer: "0.65625rem", // 10.5px  legal footer
  // Centered-card screens
  cardHeadingLarge: "1.5625rem", // 25px  You're officially Inkfree!
  cardHeading: "1.5rem", // 24px  Forgot your password?
  cardBody: "0.8125rem", // 13px  card intro copy
  cardMeta: "0.71875rem", // 11.5px  chip secondary line
  cardButton: "0.90625rem", // 14.5px  card primary button
  cardFooter: "0.6875rem", // 11px  legal line below the card
};

export const SOCIAL_BUTTON_HEIGHT = "44px";

const authButtonShape = {
  height: SOCIAL_BUTTON_HEIGHT,
  borderRadius: "8px",
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: refFont.socialButton,
};

export const authSecondaryButtonSx = {
  ...authButtonShape,
  border: `1px solid ${authRef.inputBorder}`,
  color: mobile.base[500],
  backgroundColor: "background.paper",
  "&:hover": {
    border: `1px solid ${authRef.inputBorderHover}`,
    backgroundColor: "action.hover",
  },
  "&.Mui-disabled": {
    border: `1px solid ${authRef.inputBorder}`,
  },
};

export const authSecondaryButtonWithIconSx = {
  ...authSecondaryButtonSx,
  gap: "9px",
};

export const authPrimaryButtonSx = {
  ...authButtonShape,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

export const googleButtonSlot = {
  position: "relative" as const,
  display: "flex",
  "&:focus-within .MuiButton-root": {
    outline: `2px solid ${inkfree.main}`,
    outlineOffset: "2px",
  },
  "@supports selector(:has(*))": {
    "&:focus-within:not(:has(:focus-visible)) .MuiButton-root": {
      outline: "none",
    },
  },
};

export const GOOGLE_BUTTON_WIDTH = "380";

const googleOverlayBase = {
  position: "absolute" as const,
  inset: 0,
  opacity: 0,
  overflow: "hidden",
  borderRadius: "8px",
  "& div, & iframe": {
    width: "100% !important",
    height: "100% !important",
    minWidth: "0 !important",
    maxWidth: "none !important",
  },
};

export const googleOverlaySx = (disabled: boolean) => ({
  ...googleOverlayBase,
  pointerEvents: disabled ? ("none" as const) : ("auto" as const),
  visibility: disabled ? ("hidden" as const) : ("visible" as const),
});
